import {
  MultiSelect,
  type MultiSelectProps,
  type SelectItem,
} from '@mantine/core';
import {
  type FieldValues,
  type UseControllerProps,
  useController,
} from 'react-hook-form';

type MultiSelectFieldOwnProps = {
  /**
   * Function to map the selected value to string id.
   * ID is used to display selected values in the MultiSelect.
   */
  mapValue?: (value: any) => string;

  /**
   * Function to map data to label and value.
   */
  mapData?: (data: any) => string | SelectItem;
};

export type MultiSelectFieldProps<T extends FieldValues> =
  UseControllerProps<T> &
    Omit<MultiSelectProps, 'value' | 'defaultValue'> &
    MultiSelectFieldOwnProps;

/**
 * MultiSelectField component
 *
 * @example
 * ```tsx
 * <MultiSelectField data={[{ value: '1', label: '1' }]} name="field.name" required />
 * ```
 *
 * @public
 * @param name - field name
 * @param control - form control
 * @param defaultValue - default value
 * @param rules - validation rules
 * @param shouldUnregister - unregister field when unmount
 * @param mapValue - function to map value to string id
 * @param mapData - function to map data to label and value
 * @param data - data to display in the MultiSelect
 * @param props - other props
 */
export function MultiSelectField<T extends FieldValues>({
  name,
  control,
  defaultValue,
  rules,
  shouldUnregister,
  data,
  mapValue = (value: any) => value,
  mapData = (dataToMap: any) => dataToMap,
  ...props
}: MultiSelectFieldProps<T>) {
  const {
    field: { value = [], ...field },
    fieldState,
  } = useController<T>({
    name,
    control,
    defaultValue,
    rules,
    shouldUnregister,
  });

  return (
    <MultiSelect
      data={data?.map(mapData) || []}
      value={value?.map(mapValue) || []}
      error={fieldState.error?.message}
      {...field}
      {...props}
    />
  );
}
